/*
 * Addresses in parts and a location's sub locations, shared by Head office and Settings, Business info.
 *
 * Every address is typed in parts like a client's (Street 1, Street 2, City, State, ZIP), never one free text line
 * (owner rule, 2026-11-02). A sub location is a real branch of a location with its own name, phone, ZIPs, email and
 * address (The Cleaners of Metairie under The Cleaners of New Orleans). The brand or head office sets them; the location
 * and its franchisee only see them (the server checks too).
 */
import { useState } from "react";
import { cleanZips, oneLine, stateCode, subLocationName, type AddressParts, type LocationLine } from "@steward/shared";
import { useSetSubLocations } from "../api/hq";
import { InputText } from "./fields";
import { plural } from "./format";
import { Modal, useToast } from "./overlays";
import { Banner, Button } from "./parts";

/** +15043894999 as (504) 389-4999. */
export const phoneWords = (p: string) => p.replace(/^\+1(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3");

export interface AddressDraft {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

export const draftOf = (a?: { [K in keyof AddressParts]?: string | null | undefined } | null): AddressDraft => ({
  street1: a?.street1 ?? "",
  street2: a?.street2 ?? "",
  city: a?.city ?? "",
  state: a?.state ?? "",
  zip: a?.zip ?? "",
});

/**
 * The parts of a draft, or a sentence saying what is missing. All blank is no address (null). Street 2 may stay blank.
 */
export function partsOf(d: AddressDraft): { parts: AddressParts | null; problem: string | null } {
  const t = (s: string) => s.trim();
  if (![d.street1, d.street2, d.city, d.state, d.zip].some((s) => t(s))) return { parts: null, problem: null };
  const state = stateCode(d.state) || t(d.state).toUpperCase();
  if (!t(d.street1)) return { parts: null, problem: "Type the street." };
  if (!t(d.city)) return { parts: null, problem: "Type the city." };
  if (!/^[A-Z]{2}$/.test(state)) return { parts: null, problem: "Use the 2 letter state, like LA." };
  if (!/^\d{5}$/.test(t(d.zip))) return { parts: null, problem: "Use the 5 digit ZIP." };
  return {
    parts: { street1: t(d.street1), ...(t(d.street2) ? { street2: t(d.street2) } : {}), city: t(d.city), state, zip: t(d.zip) },
    problem: null,
  };
}

/**
 * Street 1, Street 2, City, State and ZIP, laid out like a client's address. kind "billing" tells the browser these are
 * a second address, so filling the property above does not fill these too.
 */
export function AddressFields({
  value,
  onChange,
  kind,
}: {
  value: AddressDraft;
  onChange: (v: AddressDraft) => void;
  kind?: "billing";
}) {
  const auto = (token: string) => ({ name: kind ? `${kind}-${token}` : token, autoComplete: kind ? `${kind} ${token}` : token });
  const f = (k: keyof AddressDraft) => ({
    value: value[k],
    onChange: (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value }),
  });
  return (
    <div className="client-form address-parts">
      <div className="span-6">
        <InputText label="Street 1" {...auto("address-line1")} {...f("street1")} />
      </div>
      <div className="span-6">
        <InputText label="Street 2" {...auto("address-line2")} placeholder="Suite, unit" {...f("street2")} />
      </div>
      <div className="span-3 wide-phone">
        <InputText label="City" {...auto("address-level2")} {...f("city")} />
      </div>
      <div className="span-1">
        <InputText
          label="State"
          {...auto("address-level1")}
          {...f("state")}
          onBlur={() => onChange({ ...value, state: stateCode(value.state) || value.state.trim().toUpperCase().slice(0, 2) })}
        />
      </div>
      <div className="span-2">
        <InputText label="ZIP code" {...auto("postal-code")} inputMode="numeric" {...f("zip")} />
      </div>
    </div>
  );
}

/**
 * Add or change one sub location: the place name (the brand goes in front where people read it), its own phone, email,
 * address and ZIPs. Saving sends the location's whole list; Remove takes this one off.
 */
export function SubLocationSheet({
  location,
  brand,
  subs,
  index,
  onClose,
}: {
  location: { id: string; name: string };
  brand: string;
  subs: readonly LocationLine[];
  index: number;
  onClose: () => void;
}) {
  const now = subs[index];
  const [name, setName] = useState(now?.name ?? "");
  const [phone, setPhone] = useState(now ? phoneWords(now.phone) : "");
  const [email, setEmail] = useState(now?.email ?? "");
  const [address, setAddress] = useState(draftOf(now?.address));
  const [zipText, setZipText] = useState(now?.zips.join(", ") ?? "");
  const [err, setErr] = useState<string | null>(null);
  const save = useSetSubLocations();
  const toast = useToast();
  const { zips, bad } = cleanZips(zipText);
  const send = (list: LocationLine[], done: string) =>
    save.mutate(
      { companyId: location.id, lines: list },
      {
        onSuccess: () => {
          toast(done);
          onClose();
        },
        onError: (e) => setErr(e instanceof Error ? e.message : "That didn't work."),
      },
    );
  const others = () => subs.filter((_, i) => i !== index);
  const submit = () => {
    const digits = phone.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
    const where = partsOf(address);
    const problem = !name.trim()
      ? "Type the place name, like Metairie."
      : digits.length !== 10
        ? "Type the sub location's phone with its area code."
        : bad.length
          ? `Use 5 digits for each ZIP. Check ${bad.slice(0, 3).join(", ")}.`
          : where.problem;
    if (problem) {
      setErr(problem);
      return;
    }
    const mine: LocationLine = {
      name: name.trim(),
      phone: `+1${digits}`,
      zips,
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(where.parts ? { address: where.parts } : {}),
    };
    const list = others();
    list.splice(index < 0 ? list.length : index, 0, mine);
    send(list, now ? "Sub location saved" : "Sub location added");
  };
  return (
    <Modal
      title={now ? subLocationName(brand, now.name) : `New sub location of ${location.name}`}
      open
      onClose={onClose}
      footer={
        <>
          {now ? (
            <Button type="tertiary" variation="destructive" onClick={() => send(others(), "Sub location removed")}>
              Remove
            </Button>
          ) : null}
          <div className="spacer" />
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <p className="muted">
        A real branch of {location.name} with its own name, phone, email and address. Texts and emails to clients in its ZIPs come
        from it. The brand or head office sets these; the location only sees them.
      </p>
      <InputText
        label="Place"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Metairie"
        description={name.trim() ? `Shows as ${subLocationName(brand, name.trim())}` : undefined}
      />
      <InputText label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(504) 389-4999" />
      <InputText label="Email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
      <AddressFields value={address} onChange={setAddress} />
      <label className="field">
        <span className="label">ZIP codes it serves</span>
        <textarea rows={4} value={zipText} onChange={(e) => setZipText(e.target.value)} />
        <span className="muted small">Type or paste them with spaces or commas. {plural(zips.length, "ZIP code")}.</span>
      </label>
      {err ? <Banner type="error">{err}</Banner> : null}
    </Modal>
  );
}

/** A sub location's address and contact as one short line for lists. */
export const subLocationLine = (s: LocationLine) =>
  [plural(s.zips.length, "ZIP"), phoneWords(s.phone), s.email ?? "", s.address ? oneLine(s.address) : ""]
    .filter(Boolean)
    .join(", ");
